import React, { useState } from 'react';
import { X, Clock } from 'lucide-react';

/**
 * Modal admin de "Restringir temporariamente" — função independente de
 * "Interditar" (ver RyanSubdeckCard/RyanHomePage: onToggleBlock). Usado tanto
 * pra campo (RyanHomePage) quanto pra subdeck (RyanFieldPage), por isso vive
 * solto em vez de duplicado nos dois lugares. Segue o mesmo padrão visual dos
 * outros modais do Ryan (classes `ryan-modal-*`, definidas em
 * RyanFieldPage.css e RyanHomePage.css).
 */

const PRESET_MESSAGES = [
  {
    label: 'Período gratuito',
    text: 'Esse conteúdo ficará disponível assim que o período gratuito terminar.',
  },
  {
    label: 'Em atualização',
    text: 'Esse conteúdo está temporariamente indisponível porque está passando por uma atualização.',
  },
  {
    label: 'Em revisão',
    text: 'Esse conteúdo está temporariamente indisponível enquanto passa por uma revisão.',
  },
  {
    label: 'Conteúdo em preparação',
    text: 'Esse conteúdo ainda está sendo preparado e ficará disponível em breve.',
  },
];

interface RyanRestrictModalProps {
  /** Nome do campo/deck/subdeck sendo restringido — só pra contexto no título. */
  entityName: string;
  /** Mensagem já configurada, se estiver editando uma restrição já ativa. */
  initialMessage?: string;
  saving?: boolean;
  onClose: () => void;
  onConfirm: (message: string) => void;
}

const RyanRestrictModal: React.FC<RyanRestrictModalProps> = ({
  entityName, initialMessage = '', saving = false, onClose, onConfirm,
}) => {
  const [message, setMessage] = useState(initialMessage);
  const [error, setError] = useState('');

  const handleConfirm = () => {
    if (!message.trim()) { setError('Escreva ou escolha uma mensagem'); return; }
    setError('');
    onConfirm(message.trim());
  };

  return (
    <div className="ryan-modal-overlay" onClick={onClose}>
      <div className="ryan-modal" onClick={e => e.stopPropagation()}>
        <div className="ryan-modal-header">
          <h3 className="ryan-modal-title">Restringir temporariamente</h3>
          <button className="ryan-modal-close-btn" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="ryan-modal-body">
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'Inter, sans-serif', marginBottom: 4 }}>
            <strong style={{ color: '#111' }}>{entityName}</strong> ficará temporariamente indisponível para usuários comuns.
            Você pode escolher uma mensagem pronta ou escrever uma mensagem personalizada para explicar o motivo da restrição.
          </p>
          <p style={{ fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'Inter, sans-serif', marginBottom: 14 }}>
            Administradores continuam com acesso total e completo, mesmo com a restrição ativa.
          </p>

          <div className="ryan-modal-field">
            <label className="ryan-modal-label">Mensagens prontas</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {PRESET_MESSAGES.map(preset => (
                <button
                  key={preset.label}
                  type="button"
                  onClick={() => { setMessage(preset.text); setError(''); }}
                  style={{
                    padding: '6px 12px', borderRadius: 20, fontSize: 12.5, fontWeight: 600,
                    fontFamily: 'Inter, sans-serif', cursor: 'pointer',
                    border: message === preset.text ? '1.5px solid #4A90D9' : '1px solid rgba(0,0,0,0.15)',
                    background: message === preset.text ? 'rgba(74,144,217,0.1)' : 'white',
                    color: message === preset.text ? '#2E6DA8' : '#333',
                  }}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>

          <div className="ryan-modal-field">
            <label className="ryan-modal-label">Mensagem exibida para usuários comuns *</label>
            <textarea
              className="ryan-modal-textarea"
              value={message}
              onChange={e => { setMessage(e.target.value); setError(''); }}
              placeholder="Escreva a mensagem que os usuários verão, ou escolha uma pronta acima para editar…"
              rows={4}
            />
          </div>

          {error && <div className="ryan-modal-error">{error}</div>}
        </div>

        <div className="ryan-modal-footer">
          <button className="ryan-modal-cancel-btn" onClick={onClose}>Cancelar</button>
          <button className="ryan-modal-save-btn" onClick={handleConfirm} disabled={saving}>
            <Clock size={14} />
            {saving ? 'Restringindo…' : 'Restringir conteúdo'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RyanRestrictModal;
